import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook to fetch recipes for all combinations of selected ingredients
 * Returns an array of objects, each containing:
 * - ingredientIds: Array of ingredient IDs in this combination
 * - ingredientCount: Number of ingredients in this combination
 * - recipeCount: Number of recipes found for this combination
 * - recipes: Array of full recipe objects
 */
export const useRecipeCombinations = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [combinationResults, setCombinationResults] = useState([]);
    const { sessionToken } = useAuth();

    const fetchRecipeCombinations = useCallback(async (ingredientIds) => {
        if (!sessionToken) {
            setError('No session token provided');
            setCombinationResults([]);
            return [];
        }

        if (!ingredientIds || ingredientIds.length < 1) {
            setError('At least 1 ingredient required');
            setCombinationResults([]);
            return [];
        }

        setIsLoading(true);
        setError(null);

        try {
            const ingredientIdsParam = ingredientIds.join(',');
            const response = await fetch(`/api/v1/recipe-ingredient/ingredients/combo/${ingredientIdsParam}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${sessionToken}`
                }
            });

            console.log('fetchRecipeCombinations status:', response.status);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Failed to fetch recipe combinations');
            }

            const data = await response.json();
            console.log('fetchRecipeCombinations data:', data);

            if (Array.isArray(data)) {
                setCombinationResults(data);
                return data;
            } else {
                console.error('Invalid data format:', data);
                setCombinationResults([]);
                return [];
            }
        } catch (err) {
            console.error('Error fetching recipe combinations:', err);
            setError(err.message);
            setCombinationResults([]);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [sessionToken]);

    return {
        fetchRecipeCombinations,
        combinationResults,
        isLoading,
        error
    };
};

